import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { cn } from '../../lib/utils';
import {
  Calendar, Clock, User, ChevronLeft, ChevronRight, Plus, CheckCircle2,
  MapPin, Sparkles, AlertTriangle, ArrowRight, GripVertical, Timer,
  Activity, Users, DoorOpen, Stethoscope, ClipboardList, LogOut, Brain
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const FLOW_STAGES = [
  { id: 'scheduled', label: 'Scheduled', icon: Calendar, color: 'bg-slate-100 text-slate-600 border-slate-200' },
  { id: 'arrived', label: 'Arrived', icon: CheckCircle2, color: 'bg-blue-50 text-blue-600 border-blue-200' },
  { id: 'waiting', label: 'Waiting Room', icon: Clock, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  { id: 'ready_room', label: 'Ready for Room', icon: DoorOpen, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  { id: 'in_exam', label: 'In Exam Room', icon: Stethoscope, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  { id: 'with_provider', label: 'With Provider', icon: Activity, color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  { id: 'procedure', label: 'In Procedure', icon: ClipboardList, color: 'bg-pink-50 text-pink-600 border-pink-200' },
  { id: 'checkout', label: 'Ready for Checkout', icon: LogOut, color: 'bg-teal-50 text-teal-600 border-teal-200' },
  { id: 'checked_out', label: 'Checked Out', icon: CheckCircle2, color: 'bg-slate-50 text-slate-400 border-slate-200' },
];

const INITIAL_PATIENTS = [
  { id: 'PF-1', name: 'John Smith', patientId: 'P001', time: '9:00 AM', provider: 'Dr. Chen', type: 'Follow-up', stage: 'with_provider', room: 'Room 1', enteredStageAt: Date.now() - 15 * 60000, alert: null },
  { id: 'PF-2', name: 'Maria Garcia', patientId: 'P002', time: '9:30 AM', provider: 'Dr. Wilson', type: 'New Patient', stage: 'in_exam', room: 'Room 2', enteredStageAt: Date.now() - 8 * 60000, alert: null },
  { id: 'PF-3', name: 'Robert Johnson', patientId: 'P003', time: '10:00 AM', provider: 'Dr. Chen', type: 'Sick Visit', stage: 'waiting', room: null, enteredStageAt: Date.now() - 22 * 60000, alert: 'long_wait' },
  { id: 'PF-4', name: 'Emily Davis', patientId: 'P004', time: '10:30 AM', provider: 'Dr. Chen', type: 'Follow-up', stage: 'arrived', room: null, enteredStageAt: Date.now() - 5 * 60000, alert: null },
  { id: 'PF-5', name: 'James Taylor', patientId: 'P005', time: '11:00 AM', provider: 'Dr. Wilson', type: 'Wellness', stage: 'scheduled', room: null, enteredStageAt: Date.now(), alert: 'no_show_risk' },
  { id: 'PF-6', name: 'Sarah Williams', patientId: 'P006', time: '1:00 PM', provider: 'Dr. Chen', type: 'Follow-up', stage: 'scheduled', room: null, enteredStageAt: Date.now(), alert: null },
  { id: 'PF-7', name: 'David Wilson', patientId: 'P001', time: '2:00 PM', provider: 'Dr. Wilson', type: 'New Patient', stage: 'scheduled', room: null, enteredStageAt: Date.now(), alert: 'no_show_risk' },
  { id: 'PF-8', name: 'Lisa Brown', patientId: 'P002', time: '3:00 PM', provider: 'Dr. Chen', type: 'Sick Visit', stage: 'scheduled', room: null, enteredStageAt: Date.now(), alert: null },
  { id: 'PF-9', name: 'Kevin Park', patientId: 'P003', time: '9:15 AM', provider: 'Dr. Roberts', type: 'Procedure', stage: 'procedure', room: 'Room 3', enteredStageAt: Date.now() - 35 * 60000, alert: null },
  { id: 'PF-10', name: 'Nancy Lee', patientId: 'P004', time: '8:30 AM', provider: 'Dr. Chen', type: 'Annual Exam', stage: 'checkout', room: null, enteredStageAt: Date.now() - 3 * 60000, alert: null },
];

const ROOMS = [
  { id: 'room1', name: 'Room 1', occupied: true, patient: 'John Smith', timeInUse: 45 },
  { id: 'room2', name: 'Room 2', occupied: true, patient: 'Maria Garcia', timeInUse: 28 },
  { id: 'room3', name: 'Room 3', occupied: true, patient: 'Kevin Park', timeInUse: 35 },
  { id: 'room4', name: 'Room 4', occupied: false, patient: null, timeInUse: 0 },
  { id: 'room5', name: 'Room 5', occupied: false, patient: null, timeInUse: 0 },
];

const AI_SUGGESTIONS = [
  { id: 1, type: 'warning', text: 'Robert Johnson waiting 22+ min — prioritize next available room', action: 'Assign Room' },
  { id: 2, type: 'info', text: 'Room 4 available — assign next patient in queue', action: 'Assign' },
  { id: 3, type: 'warning', text: 'Dr. Wilson running 15 min behind — consider rescheduling 2:00 PM', action: 'Adjust' },
  { id: 4, type: 'risk', text: 'James Taylor high no-show risk (35%) — send confirmation', action: 'Send Reminder' },
];

const formatDuration = (ms) => {
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const EchoPatientFlow = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [patients, setPatients] = useState(INITIAL_PATIENTS);
  const [draggedId, setDraggedId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [showRooms, setShowRooms] = useState(true);
  const [showAI, setShowAI] = useState(true);
  const [roomDialog, setRoomDialog] = useState({ open: false, patientId: null });
  const [selectedRoom, setSelectedRoom] = useState('');
  const [rooms, setRooms] = useState(ROOMS);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  const handleDragStart = (e, patientId) => {
    setDraggedId(patientId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const handleDrop = (e, stageId) => {
    e.preventDefault();
    if (!draggedId) return;
    const patient = patients.find(p => p.id === draggedId);

    // If dropping to a room-needing stage and patient has no room, open dialog
    if (['in_exam', 'ready_room', 'with_provider', 'procedure'].includes(stageId) && !patient?.room) {
      setRoomDialog({ open: true, patientId: draggedId, targetStage: stageId });
      setDraggedId(null);
      return;
    }

    setPatients(prev => prev.map(p =>
      p.id === draggedId ? { ...p, stage: stageId, enteredStageAt: Date.now(), alert: null } : p
    ));

    const stageName = FLOW_STAGES.find(s => s.id === stageId)?.label;
    toast.success(`${patient?.name} → ${stageName}`);
    setDraggedId(null);
  };

  const movePatient = (patientId, direction) => {
    const patient = patients.find(p => p.id === patientId);
    const currentIndex = FLOW_STAGES.findIndex(s => s.id === patient?.stage);
    const nextIndex = direction === 'forward' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0 || nextIndex >= FLOW_STAGES.length) return;

    const nextStage = FLOW_STAGES[nextIndex];
    // If moving to in_exam/ready_room/with_provider and no room, open room dialog
    if (['in_exam', 'ready_room', 'with_provider', 'procedure'].includes(nextStage.id) && !patient.room) {
      setRoomDialog({ open: true, patientId, targetStage: nextStage.id });
      return;
    }
    setPatients(prev => prev.map(p =>
      p.id === patientId ? { ...p, stage: nextStage.id, enteredStageAt: Date.now() } : p
    ));
    toast.success(`${patient?.name} → ${nextStage.label}`);
  };

  const assignRoom = () => {
    if (!selectedRoom || !roomDialog.patientId) return;
    const patient = patients.find(p => p.id === roomDialog.patientId);
    const targetStage = roomDialog.targetStage || patient?.stage;

    // Free old room if patient had one
    if (patient?.room) {
      setRooms(prev => prev.map(r => r.name === patient.room ? { ...r, occupied: false, patient: null, timeInUse: 0 } : r));
    }

    // Assign new room
    setRooms(prev => prev.map(r => r.name === selectedRoom ? { ...r, occupied: true, patient: patient?.name, timeInUse: 0 } : r));
    setPatients(prev => prev.map(p =>
      p.id === roomDialog.patientId ? { ...p, room: selectedRoom, stage: targetStage, enteredStageAt: Date.now() } : p
    ));

    toast.success(`${patient?.name} assigned to ${selectedRoom}`);
    setRoomDialog({ open: false, patientId: null });
    setSelectedRoom('');
  };

  const openRoomDialog = (patientId) => {
    setRoomDialog({ open: true, patientId, targetStage: patients.find(p => p.id === patientId)?.stage });
    setSelectedRoom('');
  };

  const visibleStages = FLOW_STAGES.filter(stage => {
    const count = patients.filter(p => p.stage === stage.id).length;
    return count > 0 || ['scheduled', 'arrived', 'waiting', 'in_exam', 'with_provider', 'checkout'].includes(stage.id);
  });

  return (
    <div className="space-y-4" data-testid="patient-flow-board">
      {/* AI Suggestions Panel */}
      {showAI && (
        <div className="space-y-2" data-testid="ai-flow-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Brain className="h-4 w-4 text-violet-600" /><p className="text-sm font-semibold">AI Flow Intelligence</p></div>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowAI(false)}>Hide</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {AI_SUGGESTIONS.map(s => (
              <div key={s.id} className={cn("flex items-center gap-3 p-2.5 rounded-lg border text-xs",
                s.type === 'warning' ? 'border-amber-200 bg-amber-50' : s.type === 'risk' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'
              )} data-testid={`ai-suggestion-${s.id}`}>
                <Sparkles className={cn("h-3.5 w-3.5 shrink-0", s.type === 'warning' ? 'text-amber-600' : s.type === 'risk' ? 'text-red-600' : 'text-blue-600')} />
                <p className="flex-1">{s.text}</p>
                <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 shrink-0" onClick={() => toast.success(s.action + ' done')}>{s.action}</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Room & Resource Panel */}
      {showRooms && (
        <Card className="border-slate-200" data-testid="room-panel">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2"><DoorOpen className="h-4 w-4 text-slate-500" /><p className="text-xs font-semibold">Rooms</p></div>
              <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => setShowRooms(false)}>Hide</Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {rooms.map(room => (
                <div key={room.id} className={cn("flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs", room.occupied ? "border-violet-200 bg-violet-50" : "border-emerald-200 bg-emerald-50")} data-testid={`room-${room.id}`}>
                  <div className={cn("h-2 w-2 rounded-full", room.occupied ? "bg-violet-500" : "bg-emerald-500")} />
                  <span className="font-medium">{room.name}</span>
                  {room.occupied ? <span className="text-muted-foreground">{room.patient} · {room.timeInUse}m</span> : <span className="text-emerald-600">Available</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Kanban Board */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '400px' }}>
        {visibleStages.map(stage => {
          const stagePatients = patients.filter(p => p.stage === stage.id);
          const StageIcon = stage.icon;
          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-[200px]"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
              data-testid={`stage-${stage.id}`}
            >
              <div className={cn("flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2", stage.color)}>
                <StageIcon className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{stage.label}</span>
                <Badge variant="outline" className="ml-auto text-[10px] h-5 w-5 p-0 flex items-center justify-center">{stagePatients.length}</Badge>
              </div>
              <div className="bg-slate-50/50 border border-t-0 border-slate-200 rounded-b-lg p-2 space-y-2 min-h-[350px]">
                {stagePatients.map(patient => {
                  const timeInStage = now - patient.enteredStageAt;
                  const isLongWait = timeInStage > 15 * 60000 && ['waiting', 'ready_room'].includes(patient.stage);
                  return (
                    <div
                      key={patient.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, patient.id)}
                      className={cn(
                        "bg-white rounded-lg border p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow group",
                        isLongWait && "border-amber-300 bg-amber-50/30",
                        patient.alert === 'no_show_risk' && "border-red-200"
                      )}
                      data-testid={`flow-card-${patient.id}`}
                    >
                      <div className="flex items-start justify-between mb-1.5">
                        <button onClick={() => navigate(`/echo/patients/${patient.patientId}`)} className="text-xs font-semibold text-slate-800 hover:text-violet-600 hover:underline">{patient.name}</button>
                        <GripVertical className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><Clock className="h-2.5 w-2.5" />{patient.time} · {patient.type}</div>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><User className="h-2.5 w-2.5" />{patient.provider}</div>
                        <div className="flex items-center gap-1">
                          {patient.room ? (
                            <Badge variant="outline" className="text-[9px] h-4 gap-0.5 cursor-pointer hover:bg-violet-50" onClick={() => openRoomDialog(patient.id)} data-testid={`room-badge-${patient.id}`}><MapPin className="h-2 w-2" />{patient.room}</Badge>
                          ) : (
                            <button onClick={() => openRoomDialog(patient.id)} className="flex items-center gap-0.5 text-[9px] text-violet-600 hover:text-violet-800 font-medium" data-testid={`assign-room-${patient.id}`}>
                              <DoorOpen className="h-2.5 w-2.5" /> Assign Room
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-slate-100">
                        <div className={cn("flex items-center gap-1 text-[10px]", isLongWait ? "text-amber-600 font-medium" : "text-muted-foreground")}>
                          <Timer className="h-2.5 w-2.5" />{formatDuration(timeInStage)}
                        </div>
                        <div className="flex gap-0.5">
                          {patient.alert === 'long_wait' && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                          {patient.alert === 'no_show_risk' && <AlertTriangle className="h-3 w-3 text-red-500" />}
                          <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100" onClick={() => movePatient(patient.id, 'forward')}>
                            <ArrowRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {stagePatients.length === 0 && (
                  <div className="text-center py-8 text-[10px] text-muted-foreground">Drop patient here</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Room Assignment Dialog */}
      <Dialog open={roomDialog.open} onOpenChange={(v) => { setRoomDialog({ ...roomDialog, open: v }); if (!v) setSelectedRoom(''); }}>
        <DialogContent className="max-w-sm" data-testid="room-assign-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2"><DoorOpen className="h-5 w-5" /> Assign Room</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {roomDialog.patientId && (
              <div className="p-3 rounded-lg bg-slate-50 border text-xs">
                <p className="font-medium">{patients.find(p => p.id === roomDialog.patientId)?.name}</p>
                <p className="text-muted-foreground">{patients.find(p => p.id === roomDialog.patientId)?.time} · {patients.find(p => p.id === roomDialog.patientId)?.provider}</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Select Room</Label>
              <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                <SelectTrigger data-testid="room-select"><SelectValue placeholder="Choose a room..." /></SelectTrigger>
                <SelectContent>
                  {rooms.map(room => (
                    <SelectItem key={room.id} value={room.name} disabled={room.occupied}>
                      <div className="flex items-center gap-2">
                        <div className={cn("h-2 w-2 rounded-full", room.occupied ? "bg-red-400" : "bg-emerald-500")} />
                        {room.name} {room.occupied ? `(${room.patient})` : '— Available'}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {rooms.filter(r => !r.occupied).length === 0 && (
              <p className="text-xs text-red-600">All rooms are currently occupied.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRoomDialog({ open: false, patientId: null }); setSelectedRoom(''); }}>Cancel</Button>
            <Button className="bg-black hover:bg-gray-800 gap-1.5" disabled={!selectedRoom} onClick={assignRoom} data-testid="confirm-room-btn"><CheckCircle2 className="h-3.5 w-3.5" /> Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EchoPatientFlow;
